import i18n from "@/lib/i18n";

export type ApiErrorKind = "network" | "validation";

/** Codes the backend sends as `errorCode` for known data problems. Each has a translation under
 * `common.errorCodes.<code>`. Unknown codes are treated like no code at all (generic text + Sentry). */
export const KNOWN_ERROR_CODES = ["unknown_zip", "invalid_email"] as const;
export type KnownErrorCode = (typeof KNOWN_ERROR_CODES)[number];

const toKnownErrorCode = (value: unknown): KnownErrorCode | undefined =>
  KNOWN_ERROR_CODES.find((code) => code === value);

/** Distinguishes connectivity problems (worth retrying) from rejected requests (4xx),
 * so the customer gets matching, friendly guidance instead of raw backend text. */
export class ApiError extends Error {
  kind: ApiErrorKind;
  /** The backend's own message, if it sent one (e.g. "invalid email"). */
  backendMessage?: string;
  /** Set only for codes in KNOWN_ERROR_CODES. */
  errorCode?: KnownErrorCode;
  constructor(kind: ApiErrorKind, message: string, backendMessage?: string, errorCode?: KnownErrorCode) {
    super(message);
    this.name = "ApiError";
    this.kind = kind;
    this.backendMessage = backendMessage;
    this.errorCode = errorCode;
  }
}

/** fetch() wrapper: a rejected fetch (offline, DNS, CORS, timeout) becomes a network ApiError. */
export const apiFetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  try {
    return await fetch(input, init);
  } catch (error) {
    throw new ApiError("network", error instanceof Error ? error.message : "Network error");
  }
};

/** Builds the error for a non-OK response: 4xx = validation, anything else (5xx, gateway) = network-like. */
export const apiErrorFromResponse = async (response: Response, fallback: string): Promise<ApiError> => {
  let message = fallback;
  let backendMessage: string | undefined;
  let errorCode: KnownErrorCode | undefined;
  try {
    const data = await response.json();
    console.error("Simpego API error:", data);
    backendMessage = typeof data?.message === "string" && data.message ? data.message : undefined;
    message = backendMessage || fallback;
    errorCode = toKnownErrorCode(data?.errorCode);
  } catch {
    // non-JSON body (e.g. gateway error page) — keep fallback
  }
  const isValidation = response.status >= 400 && response.status < 500;
  return new ApiError(isValidation ? "validation" : "network", message, backendMessage, errorCode);
};

/** True when the customer gets a translated explanation of a known data problem (e.g. unknown ZIP) —
 * nothing for us to fix, so it must not be reported to Sentry. */
export const isHandledValidationError = (err: unknown): boolean =>
  err instanceof ApiError && err.kind === "validation" && !!err.errorCode;

/** Customer-facing text for a failed calculation/submission. Known error codes get their own translated
 * message. Other validation errors append the
 * backend's own message below the generic text so the cause (e.g. invalid email) stays visible. */
export const getCustomerErrorMessage = (err: unknown): string => {
  if (err instanceof ApiError && err.kind === "validation") {
    if (err.errorCode) return i18n.t(`common.errorCodes.${err.errorCode}`);
    const generic = i18n.t("common.errorValidation");
    return err.backendMessage ? `${generic}\n\n${err.backendMessage}` : generic;
  }
  return i18n.t("common.errorNetwork");
};
